// src/frontend/data/books/BlockchainFundamentals/data/cap_bitcoin_como_dinero.js

const CAP_BITCOIN_COMO_DINERO = {
  id: 1, // ajusta este id según tu índice real
  slug: "cap-bitcoin-como-dinero",
  title: "Bitcoin como dinero digital",
  sections: [
    {
      heading: "Dinero tradicional y Bitcoin",
      text: [
        "Para entender qué representa **Bitcoin como dinero**, primero conviene comparar el **dinero fiduciario tradicional** (euros, dólares, pesos, etc.) con las **criptomonedas** (como bitcoin, ether y muchas otras).",
        "El dinero fiduciario es emitido por bancos centrales y se apoya en leyes y políticas monetarias de gobiernos. En cambio, Bitcoin nace como una **moneda digital global y descentralizada**, diseñada para funcionar sin una autoridad central que la controle.",
        "Dicho de otra forma: mientras el dinero tradicional depende de instituciones concretas (bancos centrales, gobiernos, bancos comerciales), Bitcoin se sostiene sobre **software, criptografía, reglas de consenso y una red distribuida de participantes**.",
        "Esa diferencia hace que Bitcoin y otras criptomonedas bien diseñadas sean **resistentes a la censura**: es mucho más difícil impedir a una persona usar la red que cerrar una cuenta bancaria en un sistema centralizado."
      ]
    },
    {
      heading: "Qué tienen en común: siguen siendo dinero",
      text: [
        "A pesar de sus diferencias técnicas, tanto el dinero fiduciario como las criptomonedas cumplen las funciones básicas del dinero:"
      ],
      list: [
        "Sirven como **medio de intercambio**: permiten pagar bienes y servicios.",
        "Actúan como **unidad de cuenta**: ayudan a expresar precios y deudas.",
        "Pueden funcionar como **reserva de valor**: permiten guardar poder adquisitivo a lo largo del tiempo (con más o menos éxito, según el caso)."
      ],
      textAfter: [
        "El valor, en ambos casos, depende de una combinación de factores económicos: **oferta, demanda, confianza, utilidad, escasez, regulación, riesgo percibido**, entre otros.",
        "La diferencia clave es que en Bitcoin estas dinámicas se dan sobre una **infraestructura abierta, programable y global**, donde las reglas monetarias están codificadas desde el protocolo y no dependen únicamente de decisiones políticas."
      ]
    },
    {
      heading: "Comparando dinero fiduciario y criptomonedas",
      text: [
        "De forma muy simplificada, podemos contrastar algunos aspectos del dinero tradicional con las criptomonedas:"
      ],
      table: {
        columns: ["Dinero tradicional (fíat)", "Criptomonedas"],
        rows: [
          {
            "Dinero tradicional (fíat)": "Lo emite y regula un banco central y un gobierno.",
            "Criptomonedas": "Se producen y gestionan mediante software, nodos y consenso en red."
          },
          {
            "Dinero tradicional (fíat)": "Sistema **centralizado**: bancos y reguladores tienen control directo.",
            "Criptomonedas": "Sistema **descentralizado** (en las redes bien diseñadas): no hay una sola entidad que mande sobre la red."
          },
          {
            "Dinero tradicional (fíat)": "La política monetaria (tipos de interés, emisión, etc.) la deciden autoridades centrales.",
            "Criptomonedas": "Las reglas de emisión y validación vienen definidas en el **protocolo** (código) y son visibles para todos."
          },
          {
            "Dinero tradicional (fíat)": "Las cuentas pueden congelarse o cerrarse por decisión de una entidad.",
            "Criptomonedas": "La custodia depende de las **claves privadas** del usuario; mientras tenga sus claves, puede mover sus fondos."
          },
          {
            "Dinero tradicional (fíat)": "Su valor se ve afectado por inflación, política monetaria y confianza en el emisor.",
            "Criptomonedas": "Su valor depende de **oferta programada**, demanda de mercado, percepción de utilidad y confianza en la red."
          }
        ]
      },
      textAfter: [
        "Ambos tipos de dinero pueden convivir y, de hecho, hoy coexisten: muchas personas usan monedas tradicionales para el día a día y Bitcoin u otras criptomonedas como ahorro, cobertura o medio de pago específico en contextos digitales."
      ]
    },
    {
      heading: "La propuesta de valor de Bitcoin",
      text: [
        "En este curso ponemos el foco en **Bitcoin**, porque es la primera criptomoneda funcional y sigue siendo el ejemplo más claro de **dinero digital programable**.",
        "Algunas ideas clave de su propuesta de valor:"
      ],
      list: [
        "Es **global**: no está limitado a un país o banco; funciona allí donde haya conexión a la red.",
        "Es **programable**: las reglas de emisión, validación y transferencia están en el protocolo; los desarrolladores pueden construir herramientas encima.",
        "Es **transparente**: cualquiera puede auditar la cadena de bloques y verificar cuántas unidades existen y qué transacciones se han confirmado.",
        "Es **resistente a la censura**: no existe un botón central de “apagado” ni una oficina que pueda vetar a un usuario concreto.",
        "Tiene **oferta limitada**: el número máximo de bitcoins que existirán está acotado por el protocolo, lo que introduce una forma de escasez programada."
      ],
      textAfter: [
        "Por eso muchas personas describen a Bitcoin como una forma de dinero diseñada para la era de Internet: con reglas claras, verificables y con menos espacio para la manipulación discrecional."
      ]
    },
    {
      heading: "Criptografía: el corazón de Bitcoin",
      text: [
        "Bitcoin se apoya en la **criptografía de clave pública**. En lugar de usar nombres de usuario y contraseñas tradicionales, cada persona interactúa con la red a través de un par de claves:",
        "🔹 Una **clave privada**, que debe mantenerse en secreto y que es la que realmente otorga control sobre los fondos.",
        "🔹 Una **clave pública** (y las direcciones derivadas de ella), que se puede compartir y sirve para recibir fondos.",
        "Cuando alguien envía bitcoin, firma digitalmente la transacción con su clave privada. Los nodos de la red pueden verificar esa firma usando la clave pública correspondiente, sin necesidad de revelar la clave privada.",
        "Así se consigue algo muy poderoso: **solo quien tiene la clave privada puede mover esos bitcoins**, y al mismo tiempo la red puede verificar la autenticidad de cada transacción de forma automática."
      ]
    },
    {
      heading: "Blockchain: el gran libro contable distribuido",
      text: [
        "Cada transacción que realizas —por ejemplo, recibir bitcoin o enviarlos a otra persona— se registra en la **cadena de bloques (blockchain)**.",
        "Podemos imaginar la blockchain como un **gran libro de contabilidad público** donde cada página es un bloque, y cada bloque contiene un conjunto de transacciones.",
        "Antes de que un bloque se añada al libro mayor:",
      ],
      list: [
        "Las transacciones se agrupan y se propagan por la red.",
        "Numerosos nodos validadores revisan que cumplan las reglas del protocolo.",
        "Los mineros compiten (vía Proof of Work) para proponer el siguiente bloque válido.",
        "Cuando la mayoría de la red acepta ese bloque, se encadena criptográficamente al anterior y se vuelve parte del historial permanente."
      ],
      textAfter: [
        "De esta forma, cada nueva transacción que afecta a tus bitcoins se incorpora al historial general de la red, y cualquiera puede comprobarla. **No hay un servidor central único**, sino miles de copias sincronizadas alrededor del mundo."
      ]
    },
    {
      heading: "Bitcoin y la analogía con Internet",
      text: [
        "Una manera sencilla de visualizar la importancia de Bitcoin es compararlo con la llegada del **correo electrónico**:",
        "Antes del email, enviar una carta implicaba papel, transporte físico, intermediarios y días de espera. Con el correo electrónico, el mensaje llegó a ser casi inmediato, más barato y accesible para cualquiera con conexión.",
        "Con el dinero ocurre algo parecido: antes, mover valor a distancia requería bancos, horarios, permisos, comisiones y, a menudo, trámites lentos. Con Bitcoin, enviar fondos puede ser tan directo como enviar un mensaje: **rápido, global y sin necesidad de pedir autorización a un tercero**.",
        "Por esta razón, muchas personas llaman a Bitcoin **“dinero para la era digital”** o directamente **“dinero del futuro”**, aunque en realidad ya se utiliza en el presente en miles de casos reales."
      ]
    },
    {
      heading: "Tres ideas clave para seguir avanzando",
      text: [
        "Para entender a fondo Bitcoin, conviene quedarse con tres conceptos que profundizaremos más adelante:",
      ],
      list: [
        "**Descentralización**: no hay una única empresa “de Bitcoin”. Hay una red de nodos que aplican las mismas reglas y verifican el mismo historial.",
        "**Blockchain**: es la estructura de datos que almacena el historial de transacciones de forma ordenada, verificable e inmutable.",
        "**Minería**: es el proceso mediante el cual se añaden nuevos bloques, se asegura la red y se emiten nuevos bitcoins como recompensa."
      ],
      textAfter: [
        "En las siguientes lecciones entraremos con más detalle en la **cadena de bloques**, la **minería** y el papel de los **mineros**, para que puedas ver con claridad cómo todas estas piezas técnicas se combinan para que Bitcoin funcione como un sistema de dinero digital abierto y global."
      ]
    }
  ]
};

export default CAP_BITCOIN_COMO_DINERO;
